/**
 * FileStorageRenderer — the extracted SharedFileStorageComponent (~1,138 lines).
 *
 * Fixes applied versus the original in SharedComponentRenderer.tsx:
 *  1. Line 1803 lint suppression: replaced with thumbnailUrlsRef pattern so the
 *     unmount cleanup always revokes the latest blob URLs without re-registering
 *     the effect on every thumbnail addition.
 *  2. Line 2102 lint suppression: extracted fetchThumbnails into a useCallback with
 *     a complete, accurate dependency list so exhaustive-deps is satisfied.
 */
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Component, Viewport } from "@/types";
import { useInteractionContext } from "../InteractionContextRuntime";
import { useViewerStore } from "@/stores/viewer/viewer-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { useEditorStore } from "@/stores/editor/editor-store";
import { FileService } from "@/services/api/fileService";
import type { FileRecord, FileListParams } from "@/services/api/fileService";
import { normalizeLegacySemanticStyleColor } from "@/utils/theme-aware-legacy-colors";
import {
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  FolderPlus,
  Folder,
  Upload,
  Search,
  Grid,
  List,
  File,
  FileText,
  FileImage,
  FileSpreadsheet,
  FileCode,
  FileVideo,
  FileAudio,
  Presentation,
  Trash2,
  X,
  Download,
  Check,
  MoreVertical,
  Pencil,
  ArrowUpDown,
  Filter,
} from "lucide-react";

export interface FileStorageRendererProps {
  component: Component;
  viewport?: Viewport;
}

// ─────────────────────────────────────────────────────────────────────────────
// Error message extraction helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extracts a human-readable error message from an unknown catch value.
 * Handles Axios-style errors ({ response.data.error }) and standard Errors
 * ({ message }) without resorting to `as X` without structural proof.
 */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (typeof err !== "object" || err === null) return fallback;
  const o = err as Record<string, unknown>; // runtime-validated: typeof + null check above
  // Axios-style: err.response.data.error
  const response = typeof o.response === "object" && o.response !== null
    ? o.response as Record<string, unknown>
    : null;
  const data = response && typeof response.data === "object" && response.data !== null
    ? response.data as Record<string, unknown>
    : null;
  if (data && typeof data.error === "string") return data.error;
  // Standard Error or plain { message }
  if (typeof o.message === "string") return o.message;
  return fallback;
}

// ─────────────────────────────────────────────────────────────────────────────
// Local interfaces (not needed by any other renderer)
// ─────────────────────────────────────────────────────────────────────────────

interface FileStorageProperties {
  displayMode?: "grid" | "list";
  thumbnailSize?: "sm" | "md" | "lg" | "xl";
  title?: string;
  emptyStateMessage?: string;
  showQuotaBar?: boolean;
  showSearch?: boolean;
  showThumbnails?: boolean;
  showFileCount?: boolean;
  showFileSize?: boolean;
  showUploadDate?: boolean;
  showUploader?: boolean;
  sortDefault?: "name" | "date" | "size" | "type";
  sortDirection?: "asc" | "desc";
  allowedFileTypes?: string[];
  maxFileSize?: number;
  maxFiles?: number;
  uploadEnabled?: boolean;
  deleteEnabled?: boolean;
  folderManagementEnabled?: boolean;
  defaultScope?: "private" | "project" | "shared";
  defaultFolder?: string;
  defaultVisibility?: "private" | "org" | "public";
  encryptionDefault?: "none" | "org";
}

// ─────────────────────────────────────────────────────────────────────────────
// File utilities
// ─────────────────────────────────────────────────────────────────────────────

const formatFileSizePreview = (bytes: number): string => {
  if (bytes >= 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

const THUMB_SIZE_PX: Record<string, number> = {
  sm: 64,
  md: 120,
  lg: 180,
  xl: 260,
};

const SORT_FIELD_MAP_PREVIEW: Record<string, string> = {
  name: "filename",
  date: "createdAt",
  size: "size",
  type: "contentType",
};

const getFileIcon = (contentType: string) => {
  if (contentType.startsWith("image/")) return FileImage;
  if (contentType === "application/pdf") return FileText;
  if (contentType.startsWith("text/")) return FileText;
  if (
    contentType.includes("spreadsheet") ||
    contentType.includes("excel") ||
    contentType.includes("csv")
  )
    return FileSpreadsheet;
  if (
    contentType.includes("presentation") ||
    contentType.includes("powerpoint") ||
    contentType.includes("pptx")
  )
    return Presentation;
  if (
    contentType.includes("msword") ||
    contentType.includes("wordprocessingml")
  )
    return FileText;
  if (contentType.startsWith("video/")) return FileVideo;
  if (contentType.startsWith("audio/")) return FileAudio;
  if (
    contentType.includes("json") ||
    contentType.includes("xml") ||
    contentType.includes("javascript") ||
    contentType.includes("html") ||
    contentType.includes("css")
  )
    return FileCode;
  return File;
};

// ─────────────────────────────────────────────────────────────────────────────
// FileStorageRenderer component
// ─────────────────────────────────────────────────────────────────────────────

export const FileStorageRenderer = React.memo<FileStorageRendererProps>(
  ({ component, viewport }) => {
    const interaction = useInteractionContext();
    const isFunctionalMode = interaction?.isFunctionalMode?.();
    const { properties = {}, style = {} } = component;
    const props = properties as FileStorageProperties;

    const title = props.title || "File Storage";
    const displayMode = props.displayMode || "grid";
    const [viewMode, setViewMode] = useState<"grid" | "list">(displayMode);
    const [thumbSize, setThumbSize] = useState<"sm" | "md" | "lg" | "xl">(
      props.thumbnailSize || "md",
    );
    const showSearch = props.showSearch !== false;
    const showQuotaBar = props.showQuotaBar === true;
    const showThumbnails = props.showThumbnails !== false;
    const showFileCount = props.showFileCount !== false;
    const showFileSize = props.showFileSize !== false;
    const showUploader = props.showUploader === true;
    const uploadEnabled = props.uploadEnabled !== false;
    const deleteEnabled = props.deleteEnabled !== false;
    const bothDisabled = !uploadEnabled && !deleteEnabled;

    const viewerOrgSlug = useViewerStore((s) => s.viewerOrgSlug);
    const editorOrgSlug = useOrganizationStore(
      (s) => s.currentContext?.orgSlug,
    );
    const orgSlug = viewerOrgSlug || editorOrgSlug || null;
    const editorProject = useEditorStore((s) => s.project);
    const viewerProject = useViewerStore((s) => s.project);
    const projectId = viewerProject?.id || editorProject?.id;
    const projectName = viewerProject?.name || editorProject?.name;
    const storageTitle = projectName ? `${projectName} Files` : title;

    const [previewFiles, setPreviewFiles] = useState<FileRecord[]>([]);
    // Ref kept in sync so the file-fetch effect can read the current length
    // without adding previewFiles to its dependency array (which would create
    // an infinite loop: effect → setPreviewFiles → rerun effect).
    const previewFilesRef = useRef<FileRecord[]>([]);
    useEffect(() => {
      previewFilesRef.current = previewFiles;
    }, [previewFiles]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [previewTotal, setPreviewTotal] = useState(0);
    const [usagePercent, setUsagePercent] = useState<number | null>(null);
    const [thumbnailUrls, setThumbnailUrls] = useState<
      Record<string, string>
    >({});
    const [refreshKey, setRefreshKey] = useState(0);
    const [selectedFile, setSelectedFile] = useState<FileRecord | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSizeMenu, setShowSizeMenu] = useState(false);
    const sizeMenuRef = useRef<HTMLDivElement>(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [sortField, setSortField] = useState<
      "name" | "date" | "size" | "type"
    >("date");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
    const [filterType, setFilterType] = useState<string>("all");
    const [showSortMenu, setShowSortMenu] = useState(false);
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const sortMenuRef = useRef<HTMLDivElement>(null);
    const filterMenuRef = useRef<HTMLDivElement>(null);

    const filteredFiles = useMemo(() => {
      let files = previewFiles;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        files = files.filter((f) =>
          f.filename.toLowerCase().includes(q),
        );
      }

      if (filterType !== "all") {
        files = files.filter((f) => {
          const ct = f.contentType || "";
          switch (filterType) {
            case "images":
              return ct.startsWith("image/");
            case "documents":
              return (
                ct.includes("pdf") ||
                ct.includes("document") ||
                ct.includes("text/") ||
                ct.includes("spreadsheet") ||
                ct.includes("excel") ||
                ct.includes("csv") ||
                ct.includes("presentation") ||
                ct.includes("powerpoint") ||
                ct.includes("msword") ||
                ct.includes("wordprocessingml")
              );
            case "media":
              return ct.startsWith("video/") || ct.startsWith("audio/");
            case "code":
              return (
                ct.includes("json") ||
                ct.includes("xml") ||
                ct.includes("javascript") ||
                ct.includes("html") ||
                ct.includes("css") ||
                ct.includes("yaml") ||
                ct.includes("python") ||
                ct.includes("typescript")
              );
            case "archives":
              return (
                ct.includes("zip") ||
                ct.includes("tar") ||
                ct.includes("rar") ||
                ct.includes("gzip")
              );
            default:
              return true;
          }
        });
      }

      const sorted = [...files].sort((a, b) => {
        let cmp = 0;
        switch (sortField) {
          case "name":
            cmp = a.filename.localeCompare(b.filename);
            break;
          case "date":
            cmp =
              new Date(a.createdAt || 0).getTime() -
              new Date(b.createdAt || 0).getTime();
            break;
          case "size":
            cmp = a.size - b.size;
            break;
          case "type":
            cmp = (a.contentType || "").localeCompare(
              b.contentType || "",
            );
            break;
        }
        return sortDir === "asc" ? cmp : -cmp;
      });

      return sorted;
    }, [previewFiles, searchQuery, filterType, sortField, sortDir]);

    const folderEnabled = props.folderManagementEnabled !== false;
    const [currentFolder, setCurrentFolder] = useState<string>(
      props.defaultFolder || "",
    );
    const [subfolders, setSubfolders] = useState<
      Array<{ name: string; path: string }>
    >([]);
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [folderMenuOpen, setFolderMenuOpen] = useState<string | null>(null);
    const [renamingFolder, setRenamingFolder] = useState<string | null>(null);
    const [renameFolderName, setRenameFolderName] = useState("");
    const [_isDeletingFolder, setIsDeletingFolder] = useState(false);
    const folderMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!folderMenuOpen) return;
      const handler = (e: MouseEvent) => {
        if (
          folderMenuRef.current &&
          !folderMenuRef.current.contains(e.target as Node)
        )
          setFolderMenuOpen(null);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [folderMenuOpen]);

    useEffect(() => {
      if (!showSizeMenu) return;
      const handler = (e: MouseEvent) => {
        if (
          sizeMenuRef.current &&
          !sizeMenuRef.current.contains(e.target as Node)
        )
          setShowSizeMenu(false);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [showSizeMenu]);

    useEffect(() => {
      if (!showSortMenu && !showFilterMenu) return;
      const handler = (e: MouseEvent) => {
        if (
          showSortMenu &&
          sortMenuRef.current &&
          !sortMenuRef.current.contains(e.target as Node)
        )
          setShowSortMenu(false);
        if (
          showFilterMenu &&
          filterMenuRef.current &&
          !filterMenuRef.current.contains(e.target as Node)
        )
          setShowFilterMenu(false);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [showSortMenu, showFilterMenu]);

    useEffect(() => setViewMode(displayMode), [displayMode]);
    useEffect(
      () => setThumbSize(props.thumbnailSize || "md"),
      [props.thumbnailSize],
    );

    // Keep the latest thumbnail URL map available to unmount cleanup.
    // Capture thumbnailUrls in a ref so the unmount cleanup always sees the
    // latest map without the effect needing to re-register on every change.
    const thumbnailUrlsRef = useRef(thumbnailUrls);
    useEffect(() => {
      thumbnailUrlsRef.current = thumbnailUrls;
    }, [thumbnailUrls]);

    useEffect(() => {
      return () => {
        Object.values(thumbnailUrlsRef.current).forEach((url) => {
          if (url.startsWith("blob:")) URL.revokeObjectURL(url);
        });
      };
    }, []); // empty array is correct: cleanup runs once on unmount
    // ─────────────────────────────────────────────────────────────────────────

    const closePreview = useCallback(() => {
      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
      setSelectedFile(null);
      setPreviewUrl(null);
      setShowDeleteConfirm(false);
    }, [previewUrl]);

    const isPreviewable = useCallback((file: FileRecord) => {
      const ct = file.contentType || "";
      return ct.startsWith("image/") || ct === "application/pdf";
    }, []);

    const handleFilePreview = useCallback(
      async (file: FileRecord) => {
        if (!orgSlug || !isPreviewable(file)) return;
        setSelectedFile(file);
        setPreviewUrl(null);
        setShowDeleteConfirm(false);
        try {
          const res = await FileService.getDownloadUrl(orgSlug, file._id);
          const url = res.data?.downloadUrl;
          if (!url) return;
          if (file.contentType === "application/pdf") {
            const resp = await fetch(url);
            const blob = await resp.blob();
            const blobUrl = URL.createObjectURL(blob);
            setPreviewUrl(blobUrl);
          } else {
            setPreviewUrl(url);
          }
        } catch {
          if (thumbnailUrls[file._id]) setPreviewUrl(thumbnailUrls[file._id]);
        }
      },
      [orgSlug, isPreviewable, thumbnailUrls],
    );

    const previewableFiles = useMemo(
      () =>
        filteredFiles.filter((f) => {
          const ct = f.contentType || "";
          return ct.startsWith("image/") || ct === "application/pdf";
        }),
      [filteredFiles],
    );

    const currentPreviewIndex = useMemo(() => {
      if (!selectedFile) return -1;
      return previewableFiles.findIndex((f) => f._id === selectedFile._id);
    }, [selectedFile, previewableFiles]);

    const navigatePreview = useCallback(
      (direction: -1 | 1) => {
        if (currentPreviewIndex < 0) return;
        const nextIndex = currentPreviewIndex + direction;
        if (nextIndex < 0 || nextIndex >= previewableFiles.length) return;
        handleFilePreview(previewableFiles[nextIndex]);
      },
      [currentPreviewIndex, previewableFiles, handleFilePreview],
    );

    useEffect(() => {
      if (!selectedFile) return;
      const handler = (e: KeyboardEvent) => {
        if (e.key === "ArrowLeft") navigatePreview(-1);
        else if (e.key === "ArrowRight") navigatePreview(1);
      };
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    }, [selectedFile, navigatePreview]);

    const handleDeleteFile = useCallback(async () => {
      if (!selectedFile || !orgSlug) return;
      setIsDeleting(true);
      try {
        await FileService.deleteFile(orgSlug, selectedFile._id);
        console.log("[FileStorage:Preview] Deleted file", selectedFile._id);
        setPreviewFiles((prev) =>
          prev.filter((f) => f._id !== selectedFile._id),
        );
        setPreviewTotal((prev) => Math.max(0, prev - 1));
        setThumbnailUrls((prev) => {
          const { [selectedFile._id]: _removedUrl, ...remainingUrls } = prev;
          return remainingUrls;
        });
        closePreview();
      } catch (err: unknown) {
        console.error("[FileStorage:Preview] Delete error:", err);
      } finally {
        setIsDeleting(false);
      }
    }, [selectedFile, orgSlug, closePreview]);

    useEffect(() => {
      const handler = () => {
        console.log(
          "[FileStorage:Preview] Upload complete event received, refreshing",
        );
        setRefreshKey((k) => k + 1);
        setTimeout(() => setRefreshKey((k) => k + 1), 2000);
      };
      window.addEventListener("file-storage-upload-complete", handler);
      return () =>
        window.removeEventListener("file-storage-upload-complete", handler);
    }, []);

    useEffect(() => {
      if (!folderEnabled || !orgSlug) {
        setSubfolders([]);
        return;
      }
      FileService.listFolders(orgSlug, { parent: currentFolder, projectId })
        .then((res) =>
          setSubfolders(Array.isArray(res.data) ? res.data : []),
        )
        .catch(() => setSubfolders([]));
    }, [
      folderEnabled,
      orgSlug,
      projectId,
      currentFolder,
      refreshKey,
    ]);

    const handleCreateFolder = useCallback(async () => {
      if (!orgSlug || !newFolderName.trim()) return;
      setIsCreatingFolder(true);
      try {
        await FileService.createFolder(orgSlug, {
          name: newFolderName.trim(),
          parent: currentFolder,
          projectId,
        });
        setNewFolderName("");
        setShowCreateFolder(false);
        setRefreshKey((k) => k + 1);
      } catch (err: unknown) {
        console.error(
          "[FileStorage] Create folder error:",
          extractErrorMessage(err, "Failed to create folder"),
        );
      } finally {
        setIsCreatingFolder(false);
      }
    }, [orgSlug, newFolderName, currentFolder, projectId]);

    const handleRenameFolder = useCallback(
      async (folderPath: string) => {
        if (!orgSlug || !renameFolderName.trim()) return;
        try {
          const result = await FileService.renameFolder(orgSlug, {
            path: folderPath,
            newName: renameFolderName.trim(),
            projectId,
          });
          setRenamingFolder(null);
          setRenameFolderName("");
          if (currentFolder === folderPath && result.data) {
            setCurrentFolder(result.data.path);
          }
          setRefreshKey((k) => k + 1);
        } catch (err: unknown) {
          console.error(
            "[FileStorage] Rename folder error:",
            extractErrorMessage(err, "Failed to rename folder"),
          );
        }
      },
      [orgSlug, renameFolderName, currentFolder, projectId],
    );

    const handleDeleteFolder = useCallback(
      async (folderPath: string) => {
        if (!orgSlug) return;
        setIsDeletingFolder(true);
        try {
          await FileService.deleteFolder(orgSlug, {
            path: folderPath,
            projectId,
          });
          setFolderMenuOpen(null);
          if (
            currentFolder === folderPath ||
            currentFolder.startsWith(folderPath + "/")
          ) {
            const segments = folderPath.split("/");
            segments.pop();
            setCurrentFolder(segments.join("/"));
          }
          setRefreshKey((k) => k + 1);
        } catch (err: unknown) {
          console.error(
            "[FileStorage] Delete folder error:",
            extractErrorMessage(err, "Failed to delete folder"),
          );
        } finally {
          setIsDeletingFolder(false);
        }
      },
      [orgSlug, currentFolder, projectId],
    );

    const breadcrumbSegments = useMemo(() => {
      if (!currentFolder) return [];
      return currentFolder.split("/").filter(Boolean);
    }, [currentFolder]);

    useEffect(() => {
      if (!orgSlug) {
        console.log(
          "[FileStorage:Preview] No orgSlug available, skipping fetch",
          { componentId: component.id },
        );
        return;
      }
      const params: FileListParams = { limit: 10 };
      if (props.defaultScope) params.scope = props.defaultScope;
      if (folderEnabled) {
        params.folder = currentFolder;
        params.exact = true;
      } else if (
        props.defaultFolder !== undefined &&
        props.defaultFolder !== ""
      ) {
        params.folder = props.defaultFolder;
      }
      if (projectId) params.projectId = projectId;
      if (props.sortDefault)
        params.sortBy =
          SORT_FIELD_MAP_PREVIEW[props.sortDefault] || "createdAt";
      if (props.sortDirection) params.sortOrder = props.sortDirection;

      console.log("[FileStorage:Preview] Fetching files", {
        componentId: component.id,
        orgSlug,
        params,
      });
      // Use ref to avoid adding previewFiles to dependencies (would cause infinite loop)
      if (previewFilesRef.current.length === 0) setPreviewLoading(true);
      setPreviewError(null);

      FileService.listFiles(orgSlug, params)
        .then((response) => {
          const files = Array.isArray(response.data) ? response.data : [];
          const total = response.pagination?.total ?? files.length;
          console.log(
            `[FileStorage:Preview] Got ${files.length} files (total=${total})`,
          );
          setPreviewFiles(files);
          setPreviewTotal(total);
        })
        .catch((err: unknown) => {
          const msg = extractErrorMessage(err, "Failed to fetch files");
          console.error("[FileStorage:Preview] Fetch error:", msg);
          setPreviewError(msg);
        })
        .finally(() => setPreviewLoading(false));
    }, [
      orgSlug,
      projectId,
      props.defaultScope,
      props.defaultFolder,
      props.sortDefault,
      props.sortDirection,
      component.id,
      refreshKey,
      folderEnabled,
      currentFolder,
    ]);

    useEffect(() => {
      if (!orgSlug || !showQuotaBar) return;
      FileService.getUsage(orgSlug)
        .then((response) => {
          const usage = response.data;
          if (usage && usage.quota > 0) {
            setUsagePercent(Math.round(usage.usagePercentage));
          }
        })
        .catch(() => {
          /* quota bar is non-critical */
        });
    }, [orgSlug, showQuotaBar]);

    // Fetch thumbnails with a complete dependency list.
    // Extract fetchThumbnails into a useCallback with a complete dependency list.
    // The cancelled flag becomes a ref so the callback always reads the latest value.
    const cancelledRef = useRef(false);

    const fetchThumbnails = useCallback(
      async (files: FileRecord[], attempt = 1) => {
        if (!orgSlug || cancelledRef.current) return;
        const newUrls: Record<string, string> = {};
        const failed: FileRecord[] = [];

        await Promise.allSettled(
          files.map(async (file) => {
            try {
              const response = await FileService.getDownloadUrl(
                orgSlug,
                file._id,
              );
              const url = response.data?.downloadUrl;
              if (!url) {
                failed.push(file);
                return;
              }
              if (file.contentType === "application/pdf") {
                const resp = await fetch(url);
                const blob = await resp.blob();
                if (cancelledRef.current) return;
                newUrls[file._id] = URL.createObjectURL(blob);
              } else {
                newUrls[file._id] = url;
              }
            } catch {
              failed.push(file);
            }
          }),
        );

        if (cancelledRef.current) return;

        if (Object.keys(newUrls).length > 0) {
          setThumbnailUrls((prev) => ({ ...prev, ...newUrls }));
        }

        if (failed.length > 0 && attempt < 5) {
          const delay = Math.min(2000 * Math.pow(1.5, attempt - 1), 10000);
          setTimeout(() => {
            if (!cancelledRef.current) fetchThumbnails(failed, attempt + 1);
          }, delay);
        }
      },
      [orgSlug], // thumbnailUrls is read only for its keys — setThumbnailUrls handles merging
    );

    useEffect(() => {
      if (!orgSlug || previewFiles.length === 0 || !showThumbnails) return;

      const previewableFilesLocal = previewFiles.filter((f) => {
        const ct = f.contentType || "";
        return ct.startsWith("image/") || ct === "application/pdf";
      });
      if (previewableFilesLocal.length === 0) return;

      const missingFiles = previewableFilesLocal.filter(
        (f) => !thumbnailUrls[f._id],
      );
      if (missingFiles.length === 0) return;

      console.log(
        `[FileStorage:Preview] Fetching preview URLs for ${missingFiles.length} new files`,
      );
      cancelledRef.current = false;
      fetchThumbnails(missingFiles);
      return () => {
        cancelledRef.current = true;
      };
    }, [orgSlug, previewFiles, showThumbnails, fetchThumbnails, thumbnailUrls]);
    // ─────────────────────────────────────────────────────────────────────────

    const isMobileViewport = viewport === Viewport.MOBILE;
    const componentBackgroundColor =
      typeof style.backgroundColor === "string"
        ? normalizeLegacySemanticStyleColor("backgroundColor", style.backgroundColor, component)
        : "hsl(var(--card))";

    console.log("[FileStorage:Preview] Render", {
      componentId: component.id,
      mode: isFunctionalMode ? "functional" : "editing",
      orgSlug,
      fileCount: previewFiles.length,
    });

    return (
      <div
        className={`w-full ${isMobileViewport ? "" : "h-full"} flex flex-col bg-card text-card-foreground border border-border rounded-lg ${isMobileViewport ? "" : "overflow-hidden"}`}
        data-testid="fs-preview"
        style={{
          backgroundColor: componentBackgroundColor,
          borderRadius:
            typeof style.borderRadius === "string"
              ? style.borderRadius
              : "8px",
          fontSize:
            typeof style.fontSize === "number" ? style.fontSize : 14,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
          <div className="flex items-center gap-1 min-w-0 overflow-hidden">
            {folderEnabled ? (
              <>
                <button
                  onClick={() => setCurrentFolder("")}
                  className="flex items-center gap-1 shrink-0 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="fs-breadcrumb-root"
                >
                  <FolderOpen className="h-4 w-4" />
                  <span className="font-medium">{storageTitle}</span>
                </button>
                {breadcrumbSegments.map((seg, i) => {
                  const segPath = breadcrumbSegments.slice(0, i + 1).join("/");
                  const isLast = i === breadcrumbSegments.length - 1;
                  return (
                    <React.Fragment key={segPath}>
                      <ChevronRight className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                      {isLast ? (
                        <span className="text-sm font-medium text-foreground truncate max-w-[120px]">
                          {seg}
                        </span>
                      ) : (
                        <button
                          onClick={() => setCurrentFolder(segPath)}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors truncate max-w-[100px]"
                          data-testid={`fs-breadcrumb-${i}`}
                        >
                          {seg}
                        </button>
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            ) : (
              <>
                <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-medium text-foreground text-sm truncate">
                  {storageTitle}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {showFileCount && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                data-testid="fs-file-count"
              >
                {subfolders.length > 0 ? `${subfolders.length} folders, ` : ""}
                {previewTotal} {previewTotal === 1 ? "file" : "files"}
              </span>
            )}
            <div className="flex items-center gap-0.5 rounded bg-muted p-0.5">
              <div className="relative" ref={sizeMenuRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (viewMode === "grid") {
                      setShowSizeMenu(!showSizeMenu);
                    } else {
                      setViewMode("grid");
                    }
                  }}
                  className={`p-1 rounded transition-colors ${viewMode === "grid" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="fs-view-grid-btn"
                >
                  <Grid className="h-3.5 w-3.5" />
                </button>
                {showSizeMenu && (
                  <div className="absolute top-full right-0 mt-1 bg-popover text-popover-foreground rounded-md shadow-lg border border-border py-1 z-20 min-w-[100px]">
                    {(
                      [
                        ["sm", "Small"],
                        ["md", "Medium"],
                        ["lg", "Large"],
                        ["xl", "Extra Large"],
                      ] as const
                    ).map(([size, label]) => (
                      <button
                        key={size}
                        onClick={(e) => {
                          e.stopPropagation();
                          setThumbSize(size);
                          setShowSizeMenu(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${thumbSize === size ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                        data-testid={`fs-size-${size}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setViewMode("list");
                  setShowSizeMenu(false);
                }}
                className={`p-1 rounded transition-colors ${viewMode === "list" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="fs-view-list-btn"
              >
                <List className="h-3.5 w-3.5" />
              </button>
            </div>
            {/* Sort dropdown */}
            <div className="relative" ref={sortMenuRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSortMenu(!showSortMenu);
                  setShowFilterMenu(false);
                }}
                className={`p-1.5 border rounded text-xs hover:bg-muted flex items-center gap-1 transition-colors ${showSortMenu ? "border-border bg-muted text-foreground" : "border-border text-muted-foreground"}`}
                data-testid="fs-sort-btn"
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
              </button>
              {showSortMenu && (
                <div className="absolute top-full right-0 mt-1 bg-popover text-popover-foreground rounded-md shadow-lg border border-border py-1 z-20 min-w-[140px]">
                  <div className="px-2.5 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Sort by
                  </div>
                  {(
                    [
                      ["name", "Name"],
                      ["date", "Date Added"],
                      ["size", "Size"],
                      ["type", "File Type"],
                    ] as const
                  ).map(([field, label]) => (
                    <button
                      key={field}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (sortField === field) {
                          setSortDir(sortDir === "asc" ? "desc" : "asc");
                        } else {
                          setSortField(field);
                          setSortDir(field === "name" ? "asc" : "desc");
                        }
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between transition-colors ${sortField === field ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                      data-testid={`fs-sort-${field}`}
                    >
                      <span>{label}</span>
                      {sortField === field && (
                        <span className="text-[10px] text-muted-foreground">
                          {sortDir === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Filter dropdown */}
            <div className="relative" ref={filterMenuRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFilterMenu(!showFilterMenu);
                  setShowSortMenu(false);
                }}
                className={`p-1.5 border rounded text-xs hover:bg-muted flex items-center gap-1 transition-colors ${filterType !== "all" ? "border-emerald-500/35 bg-emerald-500/12 text-emerald-300" : showFilterMenu ? "border-border bg-muted text-foreground" : "border-border text-muted-foreground"}`}
                data-testid="fs-filter-btn"
              >
                <Filter className="w-3.5 h-3.5" />
              </button>
              {showFilterMenu && (
                <div className="absolute top-full right-0 mt-1 bg-popover text-popover-foreground rounded-md shadow-lg border border-border py-1 z-20 min-w-[140px]">
                  <div className="px-2.5 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    File type
                  </div>
                  {(
                    [
                      ["all", "All Files"],
                      ["images", "Images"],
                      ["documents", "Documents"],
                      ["media", "Audio / Video"],
                      ["code", "Code"],
                      ["archives", "Archives"],
                    ] as const
                  ).map(([type, label]) => (
                    <button
                      key={type}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilterType(type);
                        setShowFilterMenu(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between transition-colors ${filterType === type ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                      data-testid={`fs-filter-${type}`}
                    >
                      <span>{label}</span>
                      {filterType === type && (
                        <Check className="w-3 h-3 text-emerald-300" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {folderEnabled && isFunctionalMode && (
              <button
                onClick={() => setShowCreateFolder(true)}
                className="p-1.5 border border-border text-muted-foreground rounded text-xs hover:bg-muted hover:text-foreground flex items-center gap-1"
                data-testid="fs-create-folder-btn"
              >
                <FolderPlus className="w-3.5 h-3.5" />
              </button>
            )}
            {isFunctionalMode ? (
              <button
                onClick={() =>
                  interaction?.handlers?.onOpenFileManager?.(
                    component.id,
                    folderEnabled ? currentFolder : undefined,
                  )
                }
                className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs font-medium hover:bg-emerald-700 flex items-center gap-1.5"
                data-testid="file-storage-manage-btn"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload
              </button>
            ) : (
              <button
                disabled
                className="px-3 py-1.5 bg-muted text-muted-foreground rounded text-xs font-medium cursor-not-allowed opacity-50 flex items-center gap-1.5"
                data-testid="file-storage-manage-btn-disabled"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload
              </button>
            )}
          </div>
        </div>

        {/* Inline create folder input */}
        {showCreateFolder && (
          <div
            className="px-3 py-1.5 border-b border-border"
            data-testid="fs-create-folder-row"
          >
            <div className="flex items-center gap-2 px-2.5 py-1 bg-muted rounded-md text-xs border border-transparent focus-within:border-border transition-colors">
              <Folder className="h-3 w-3 text-amber-500 shrink-0" />
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateFolder();
                  if (e.key === "Escape") {
                    setShowCreateFolder(false);
                    setNewFolderName("");
                  }
                }}
                placeholder="Folder name"
                className="bg-transparent outline-none border-none focus:ring-0 w-full text-foreground placeholder:text-muted-foreground"
                autoFocus
                data-testid="fs-folder-name-input"
              />
              <button
                onClick={handleCreateFolder}
                disabled={isCreatingFolder || !newFolderName.trim()}
                className="p-0.5 rounded text-emerald-300 hover:text-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                data-testid="fs-folder-create-confirm"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => {
                  setShowCreateFolder(false);
                  setNewFolderName("");
                }}
                className="p-0.5 rounded text-muted-foreground hover:text-foreground shrink-0"
                data-testid="fs-folder-create-cancel"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Permission indicator (read-only) */}
        {bothDisabled && (
          <div className="flex items-center justify-end px-3 py-1.5 border-b border-border">
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground"
              data-testid="fs-readonly-badge"
            >
              Read-only
            </span>
          </div>
        )}

        {/* Search bar */}
        {showSearch && (
          <div
            className="px-3 py-1.5 border-b border-border"
            data-testid="fs-search-bar"
          >
            <div className="flex items-center gap-2 px-2.5 py-1 bg-muted rounded-md text-xs border border-transparent focus-within:border-border transition-colors">
              <Search className="h-3 w-3 text-muted-foreground shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search files..."
                className="bg-transparent outline-none border-none focus:ring-0 w-full text-foreground placeholder:text-muted-foreground"
                data-testid="fs-search-input"
              />
            </div>
          </div>
        )}

        {/* File list/grid content */}
        <div
          className={
            isMobileViewport
              ? "min-h-[80px]"
              : "flex-1 overflow-hidden min-h-[80px]"
          }
        >
          {previewLoading ? (
            <div
              className="flex items-center justify-center h-full py-6"
              data-testid="fs-loading"
            >
              <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
            </div>
          ) : previewError ? (
            <div
              className="flex flex-col items-center justify-center h-full py-6 text-center px-3"
              data-testid="fs-error"
            >
              <AlertCircle className="h-5 w-5 text-red-400 mb-1" />
              <p className="text-[11px] text-red-500">{previewError}</p>
            </div>
          ) : filteredFiles.length === 0 && subfolders.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center h-full py-6 text-center px-3"
              data-testid="fs-empty"
            >
              <FolderOpen className="h-8 w-8 text-muted-foreground/45 mb-2" />
              <p className="text-xs text-muted-foreground">
                {searchQuery
                  ? "No matching files"
                  : currentFolder
                    ? "This folder is empty"
                    : props.emptyStateMessage || "No files yet"}
              </p>
            </div>
          ) : viewMode === "grid" ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill, minmax(${THUMB_SIZE_PX[thumbSize]}px, 1fr))`,
                gap: "8px",
              }}
              className={`p-2.5 ${isMobileViewport ? "" : "overflow-y-auto h-full"}`}
              data-testid="fs-grid-view"
            >
              {subfolders.map((folder) => (
                <div
                  key={`folder-${folder.path}`}
                  className="group relative flex flex-col border border-amber-500/25 rounded-lg overflow-hidden bg-amber-500/10 shadow-sm cursor-pointer hover:border-amber-500/45 hover:shadow-md transition-all duration-150"
                  data-testid="fs-grid-folder"
                  onClick={() => {
                    if (!renamingFolder && !folderMenuOpen)
                      setCurrentFolder(folder.path);
                  }}
                >
                  {isFunctionalMode && (
                    <div
                      className="absolute top-1 right-1 z-10"
                      ref={
                        folderMenuOpen === folder.path
                          ? folderMenuRef
                          : undefined
                      }
                    >
                      <button
                        className="p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-amber-500/15 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderMenuOpen(
                            folderMenuOpen === folder.path
                              ? null
                              : folder.path,
                          );
                        }}
                      >
                        <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                      {folderMenuOpen === folder.path && (
                        <div className="absolute right-0 top-full mt-1 bg-popover text-popover-foreground border border-border rounded-lg shadow-lg py-1 min-w-[120px]">
                          <button
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-popover-foreground hover:bg-muted"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFolderMenuOpen(null);
                              setRenamingFolder(folder.path);
                              setRenameFolderName(folder.name);
                            }}
                          >
                            <Pencil className="h-3 w-3" /> Rename
                          </button>
                          <button
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(folder.path);
                            }}
                          >
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="aspect-[4/3] flex items-center justify-center">
                    <Folder className="h-10 w-10 text-amber-400" />
                  </div>
                  <div className="px-2 py-1.5 text-center">
                    {renamingFolder === folder.path ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleRenameFolder(folder.path);
                        }}
                        className="flex items-center gap-1"
                      >
                        <input
                          autoFocus
                          value={renameFolderName}
                          onChange={(e) =>
                            setRenameFolderName(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              setRenamingFolder(null);
                              setRenameFolderName("");
                            }
                          }}
                          className="w-full text-[11px] px-1 py-0.5 border border-amber-500/45 bg-background text-foreground rounded focus:outline-none focus:ring-1 focus:ring-amber-400"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button
                          type="submit"
                          className="shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Check className="h-3 w-3 text-green-300" />
                        </button>
                        <button
                          type="button"
                          className="shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRenamingFolder(null);
                            setRenameFolderName("");
                          }}
                        >
                          <X className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </form>
                    ) : (
                      <p className="text-[11px] font-medium text-foreground truncate">
                        {folder.name}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {filteredFiles.map((file) => {
                const Icon = getFileIcon(file.contentType || "");
                return (
                  <div
                    key={file._id}
                    className={`flex flex-col border border-border rounded-lg overflow-hidden bg-card shadow-sm${isPreviewable(file) ? " cursor-pointer hover:border-muted-foreground/40 hover:shadow-md transition-all duration-150" : ""}`}
                    data-testid="fs-grid-card"
                    onClick={() => handleFilePreview(file)}
                  >
                    {showThumbnails && (
                      <div className="aspect-[4/3] bg-muted flex items-center justify-center overflow-hidden relative">
                        {!thumbnailUrls[file._id] && (
                          <Icon className="h-8 w-8 text-muted-foreground/45" />
                        )}
                        {thumbnailUrls[file._id] &&
                        file.contentType === "application/pdf" ? (
                          <iframe
                            src={`${thumbnailUrls[file._id]}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                            title={file.filename}
                            className="absolute inset-0 w-[200%] h-[200%] border-0 pointer-events-none origin-top-left scale-50"
                            tabIndex={-1}
                          />
                        ) : thumbnailUrls[file._id] ? (
                          <img
                            src={thumbnailUrls[file._id]}
                            alt={file.filename}
                            className="absolute inset-0 w-full h-full object-contain"
                            onError={(e) => {
                              (
                                e.target as HTMLImageElement
                              ).style.display = "none";
                            }}
                          />
                        ) : null}
                      </div>
                    )}
                    <div className="px-2 py-1.5 text-center">
                      <p className="text-[11px] font-medium text-foreground truncate">
                        {file.filename}
                      </p>
                      {(showFileSize || file.updatedAt) && (
                        <p className="text-[10px] text-muted-foreground">
                          {showFileSize && formatFileSizePreview(file.size)}
                          {showFileSize && file.updatedAt && " · "}
                          {file.updatedAt &&
                            new Date(file.updatedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className={`flex flex-col divide-y divide-border ${isMobileViewport ? "" : "overflow-y-auto h-full"}`}
              data-testid="fs-list-view"
            >
              {subfolders.map((folder) => (
                <div
                  key={`folder-${folder.path}`}
                  className="group relative flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-amber-500/10 transition-colors"
                  data-testid="fs-list-folder"
                  onClick={() => {
                    if (!renamingFolder && !folderMenuOpen)
                      setCurrentFolder(folder.path);
                  }}
                >
                  <Folder className="h-4 w-4 text-amber-400 shrink-0" />
                  {renamingFolder === folder.path ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleRenameFolder(folder.path);
                      }}
                      className="flex items-center gap-1 flex-1"
                    >
                      <input
                        autoFocus
                        value={renameFolderName}
                        onChange={(e) => setRenameFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            setRenamingFolder(null);
                            setRenameFolderName("");
                          }
                        }}
                        className="flex-1 text-xs px-1.5 py-0.5 border border-amber-500/45 bg-background text-foreground rounded focus:outline-none focus:ring-1 focus:ring-amber-400"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button
                        type="submit"
                        className="shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Check className="h-3 w-3 text-green-300" />
                      </button>
                      <button
                        type="button"
                        className="shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenamingFolder(null);
                          setRenameFolderName("");
                        }}
                      >
                        <X className="h-3 w-3 text-muted-foreground" />
                      </button>
                    </form>
                  ) : (
                    <span className="text-xs text-foreground flex-1 truncate font-medium">
                      {folder.name}
                    </span>
                  )}
                  {isFunctionalMode && !renamingFolder && (
                    <div
                      className="relative"
                      ref={
                        folderMenuOpen === folder.path
                          ? folderMenuRef
                          : undefined
                      }
                    >
                      <button
                        className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-amber-500/15 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderMenuOpen(
                            folderMenuOpen === folder.path
                              ? null
                              : folder.path,
                          );
                        }}
                      >
                        <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                      {folderMenuOpen === folder.path && (
                        <div className="absolute right-0 top-full mt-1 bg-popover text-popover-foreground border border-border rounded-lg shadow-lg py-1 min-w-[120px] z-20">
                          <button
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-popover-foreground hover:bg-muted"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFolderMenuOpen(null);
                              setRenamingFolder(folder.path);
                              setRenameFolderName(folder.name);
                            }}
                          >
                            <Pencil className="h-3 w-3" /> Rename
                          </button>
                          <button
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(folder.path);
                            }}
                          >
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {!isFunctionalMode && (
                    <ChevronRight className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                  )}
                </div>
              ))}
              {filteredFiles.map((file) => {
                const Icon = getFileIcon(file.contentType || "");
                return (
                  <div
                    key={file._id}
                    className={`flex items-center gap-2 px-3 py-2${isPreviewable(file) ? " cursor-pointer hover:bg-muted transition-colors" : ""}`}
                    data-testid="fs-list-row"
                    onClick={() => handleFilePreview(file)}
                  >
                    {thumbnailUrls[file._id] &&
                    file.contentType !== "application/pdf" ? (
                      <div className="h-6 w-6 rounded overflow-hidden shrink-0 relative bg-muted">
                        <img
                          src={thumbnailUrls[file._id]}
                          alt={file.filename}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (
                              e.target as HTMLImageElement
                            ).style.display = "none";
                          }}
                        />
                      </div>
                    ) : (
                      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="text-xs text-foreground flex-1 truncate">
                      {file.filename}
                    </span>
                    {(showFileSize || file.updatedAt) && (
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {showFileSize && formatFileSizePreview(file.size)}
                        {showFileSize && file.updatedAt && " · "}
                        {file.updatedAt &&
                          new Date(file.updatedAt).toLocaleDateString()}
                      </span>
                    )}
                    {showUploader && (
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {file.uploadedBy || "Unknown"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quota bar */}
        {showQuotaBar && (
          <div
            className="px-3 py-2 border-t border-border"
            data-testid="fs-quota-bar"
          >
            <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
              <span>Storage</span>
              <span>
                {usagePercent !== null ? `${usagePercent}%` : "—"}
              </span>
            </div>
            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{
                  width:
                    usagePercent !== null ? `${usagePercent}%` : "0%",
                }}
              />
            </div>
          </div>
        )}

        {/* File Preview Modal (images + PDFs) */}
        {selectedFile && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={closePreview}
            data-testid="fs-file-preview-backdrop"
          >
            {selectedFile.contentType === "application/pdf" ? (
              <div
                className="relative rounded-lg bg-gray-900 flex flex-col overflow-hidden w-[80vw] h-[85vh]"
                onClick={(e) => e.stopPropagation()}
                data-testid="fs-file-preview-modal"
              >
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-900 shrink-0">
                  {isFunctionalMode && deleteEnabled && (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="p-1.5 rounded-full text-white/70 hover:text-red-400 hover:bg-white/10 transition-colors"
                      data-testid="fs-preview-delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">
                      {selectedFile.filename}
                    </p>
                    <p className="text-white/50 text-xs">
                      {formatFileSizePreview(selectedFile.size)}
                      {selectedFile.updatedAt &&
                        ` · ${new Date(selectedFile.updatedAt).toLocaleDateString()}`}
                    </p>
                  </div>
                  <button
                    onClick={closePreview}
                    className="p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0"
                    data-testid="fs-preview-close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="relative flex-1 min-h-0">
                  {previewUrl ? (
                    <iframe
                      src={previewUrl}
                      title={selectedFile.filename}
                      className="w-full h-full border-0"
                      data-testid="fs-preview-pdf"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
                    </div>
                  )}
                </div>
                {showDeleteConfirm && (
                  <div
                    className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm"
                    data-testid="fs-delete-confirm-overlay"
                  >
                    <Trash2 className="w-10 h-10 text-red-400 mb-3" />
                    <p className="text-white text-sm font-medium mb-1">
                      Delete this file?
                    </p>
                    <p className="text-white/60 text-xs mb-4 px-4 text-center truncate max-w-[300px]">
                      {selectedFile.filename}
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-4 py-1.5 rounded-md bg-white/20 text-white text-sm hover:bg-white/30 transition-colors"
                        data-testid="fs-delete-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteFile}
                        disabled={isDeleting}
                        className="px-4 py-1.5 rounded-md bg-red-600 text-white text-sm hover:bg-red-700 transition-colors disabled:opacity-50"
                        data-testid="fs-delete-confirm"
                      >
                        {isDeleting ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div
                className="relative overflow-hidden rounded-lg bg-gray-900 flex items-center justify-center max-w-[80vw] max-h-[80vh] min-w-[320px] min-h-[240px]"
                onClick={(e) => e.stopPropagation()}
                data-testid="fs-file-preview-modal"
              >
                <button
                  onClick={closePreview}
                  className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  data-testid="fs-preview-close"
                >
                  <X className="w-4 h-4" />
                </button>
                {isFunctionalMode && deleteEnabled && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="absolute top-2 left-2 z-10 p-1.5 rounded-full bg-black/50 text-white hover:bg-red-600 transition-colors"
                    data-testid="fs-preview-delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                {previewUrl && (
                  <button
                    onClick={async () => {
                      try {
                        const resp = await fetch(previewUrl);
                        const blob = await resp.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = selectedFile.filename;
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch {
                        window.open(previewUrl, "_blank");
                      }
                    }}
                    className="absolute top-2 right-10 z-10 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                    data-testid="fs-preview-download"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                )}
                {currentPreviewIndex > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigatePreview(-1);
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                    data-testid="fs-preview-prev"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                {currentPreviewIndex < previewableFiles.length - 1 &&
                  currentPreviewIndex >= 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigatePreview(1);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                      data-testid="fs-preview-next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  )}
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={selectedFile.filename}
                    className="max-w-[80vw] max-h-[80vh] object-contain"
                    style={{
                      backgroundImage:
                        "repeating-conic-gradient(#d1d5db 0% 25%, #f3f4f6 0% 50%)",
                      backgroundSize: "16px 16px",
                    }}
                    data-testid="fs-preview-image"
                  />
                ) : (
                  <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
                )}
                <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent pointer-events-none">
                  <p className="text-white text-sm truncate">
                    {selectedFile.filename}
                  </p>
                  <p className="text-white/60 text-xs">
                    {formatFileSizePreview(selectedFile.size)}
                    {selectedFile.updatedAt &&
                      ` · ${new Date(selectedFile.updatedAt).toLocaleDateString()}`}
                  </p>
                </div>
                {showDeleteConfirm && (
                  <div
                    className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm"
                    data-testid="fs-delete-confirm-overlay"
                  >
                    <Trash2 className="w-10 h-10 text-red-400 mb-3" />
                    <p className="text-white text-sm font-medium mb-1">
                      Delete this file?
                    </p>
                    <p className="text-white/60 text-xs mb-4 px-4 text-center truncate max-w-[300px]">
                      {selectedFile.filename}
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-4 py-1.5 rounded-md bg-white/20 text-white text-sm hover:bg-white/30 transition-colors"
                        data-testid="fs-delete-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteFile}
                        disabled={isDeleting}
                        className="px-4 py-1.5 rounded-md bg-red-600 text-white text-sm hover:bg-red-700 transition-colors disabled:opacity-50"
                        data-testid="fs-delete-confirm"
                      >
                        {isDeleting ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

FileStorageRenderer.displayName = "FileStorageRenderer";
